import React, { useMemo, useState } from "react";
import {
  getFDDQueueByType,
  getFileBlob,
  setItemStatus,
} from "../../store/fddStore";
import { ensureSharePointRefs } from "../../runtime/resolveQueuedFile";
import { getToken } from "../../../utils/tokenHelper";
import { CONTRACT_TYPES, findContractType } from "./contractTypes";
import { apiFetch } from "../../../api/fddClient";

type Props = {
  projectId: string;
  projectName?: string;
  queueVersion: number;
  onQueueChanged: () => void;
};

async function fetchSharePointFile(
  driveId: string,
  itemId: string,
  fileName: string,
): Promise<File> {
  const token = await getToken();
  const url = `https://graph.microsoft.com/v1.0/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    throw new Error(`Failed to download "${fileName}" from SharePoint (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || "application/pdf" });
}

const ContractReviewPanel: React.FC<Props> = ({
  projectId,
  projectName,
  queueVersion,
  onQueueChanged,
}) => {
  const queue = useMemo(
    () => getFDDQueueByType(projectId, "contract-review"),
    [projectId, queueVersion],
  );

  const firstAvailable = CONTRACT_TYPES.find((t) => t.available)?.id ?? "ppa";
  const [contractTypeId, setContractTypeId] = useState<string>(firstAvailable);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState(false);

  const selectedType = findContractType(contractTypeId);
  // Anything that hasn't already errored is a candidate to run. The run
  // handler resolves pending_upload items at run-time (Graph search by
  // file name) so the user doesn't have to wait for an upload-promotion
  // path to fire before clicking Run.
  const candidateCount = queue.filter((q) => q.status !== "error").length;
  const canRun = candidateCount > 0 && !running && selectedType?.available === true;

  const handleRun = async () => {
    const runnable = queue.filter((q) => q.status !== "error");
    if (!runnable.length) {
      setError("Queue is empty. Add at least one contract PDF.");
      return;
    }
    if (!selectedType?.available) {
      setError("Selected contract type isn't available yet.");
      return;
    }
    setRunning(true);
    setError(null);
    setDownloaded(false);

    const formData = new FormData();
    formData.append("contractType", selectedType.id);
    if (projectName) formData.append("projectName", projectName);

    const itemsForStatus: { id: string }[] = [];

    try {
      for (const queuedItem of runnable) {
        setItemStatus(projectId, queuedItem.id, "in_progress");
        itemsForStatus.push({ id: queuedItem.id });

        // Resolve refs at run-time. If the queue item is still
        // pending_upload (or missing refs for any reason), this searches
        // the project's SharePoint drive for the file by exact name and
        // stamps the refs onto the queue item.
        const item = await ensureSharePointRefs(queuedItem, projectId);

        const localFile = getFileBlob(item.id);
        let file: File;
        if (localFile) {
          file = localFile;
        } else if (item.driveId && item.fileId) {
          file = await fetchSharePointFile(item.driveId, item.fileId, item.fileName);
        } else {
          throw new Error(
            `"${item.fileName}" was queued for analysis but couldn't be found — not in this browser session, no SharePoint refs on the queue item, and a search of the project's VDR didn't find it by name. Re-queue from the FDD SharePoint picker.`,
          );
        }
        formData.append("files", file, item.fileName);
      }

      onQueueChanged();

      const res = await apiFetch("/api/contract-review", { method: "POST", body: formData });
      if (!res.ok) {
        let message = `Extraction failed (HTTP ${res.status})`;
        try {
          const errBody = await res.json();
          if (errBody?.error) message = errBody.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      // Job-queue pattern (same as the databook builder): 202 { jobId }, poll
      // status, then download the result — keeps each HTTP request short so
      // Azure's 230s front-end limit can't kill a long extraction.
      const startJson = await res.json().catch(() => ({} as any));
      const jobId: string | undefined = startJson?.jobId;
      if (!jobId) throw new Error("Extraction started but no job ID returned.");

      let attempts = 0;
      while (true) {
        attempts++;
        await new Promise((r) => setTimeout(r, attempts < 3 ? 1000 : 3000));
        const sRes = await apiFetch(`/api/contract-review/jobs/${jobId}`).catch(() => null);
        if (!sRes || !sRes.ok) continue;
        const sJson = await sRes.json().catch(() => null);
        if (!sJson) continue;
        if (sJson.status === "error") throw new Error(sJson.error || "Extraction failed.");
        if (sJson.status === "done") break;
      }

      const blobRes = await apiFetch(`/api/contract-review/jobs/${jobId}/result`);
      if (!blobRes.ok) throw new Error(`Result download failed (HTTP ${blobRes.status})`);
      const blob = await blobRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedType.label.toLowerCase()}-summary.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const completedAt = new Date().toISOString();
      for (const item of itemsForStatus) {
        setItemStatus(projectId, item.id, "completed", { completedAt });
      }
      setDownloaded(true);
      onQueueChanged();
    } catch (err: any) {
      const message = err?.message || "Extraction failed";
      setError(message);
      for (const item of itemsForStatus) {
        setItemStatus(projectId, item.id, "error", { error: message });
      }
      onQueueChanged();
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      style={{
        border: "1px solid #cfe6c7",
        borderRadius: 10,
        background: "#f8fdf6",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ fontWeight: 700, color: "#2f6b2f", fontSize: 13 }}>
        Contract Review
      </div>

      {/* Contract type selector */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label
          htmlFor="contract-type-select"
          style={{ fontSize: 12, fontWeight: 600, color: "#2f6b2f" }}
        >
          Contract type
        </label>
        <select
          id="contract-type-select"
          value={contractTypeId}
          onChange={(e) => setContractTypeId(e.target.value)}
          disabled={running}
          style={{
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #cfe6c7",
            background: "#ffffff",
            color: "#2f6b2f",
            fontSize: 13,
            fontWeight: 600,
            cursor: running ? "not-allowed" : "pointer",
          }}
        >
          {CONTRACT_TYPES.map((t) => (
            <option key={t.id} value={t.id} disabled={!t.available}>
              {t.longLabel}
              {!t.available ? " (coming soon)" : ""}
            </option>
          ))}
        </select>
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          Applies to every queued file in this run. Mix types by running separate batches.
        </div>
      </div>

      <button
        onClick={handleRun}
        disabled={!canRun}
        style={{
          padding: 12,
          borderRadius: 10,
          border: "none",
          background: !canRun ? "#9ca3af" : "#6aa84f",
          color: "#fff",
          fontSize: 14,
          fontWeight: 700,
          cursor: !canRun ? "not-allowed" : "pointer",
        }}
      >
        {running
          ? `Extracting ${selectedType?.label} terms from ${candidateCount} file${candidateCount === 1 ? "" : "s"}…`
          : `Create ${selectedType?.label ?? "Contract"} Summary (${candidateCount})`}
      </button>

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            padding: 10,
            color: "#dc2626",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {downloaded && !error && (
        <div
          style={{
            background: "#f0f9eb",
            border: "1px solid #6aa84f",
            borderRadius: 8,
            padding: 10,
            color: "#15803d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {selectedType?.label} summary downloaded — check your Downloads folder.
        </div>
      )}
    </section>
  );
};

export default ContractReviewPanel;
